import { TRANSITION_TYPES, type TransitionType } from '@liveforge/shared';
import { useStudioStore } from '../../stores/studio-store';

/**
 * The take column, between the two monitors: how to get from preview to air,
 * and the button that does it.
 *
 * It sits between them rather than under them because that is what it does -
 * preview on the left, air on the right, and the control that moves one into
 * the other in the middle. The position is the explanation.
 *
 * Hidden in quick mode, where there is nothing to stage and a scene click is
 * already the cut.
 *
 * The transition type and duration are stored and shown now, but the renderer
 * still cuts: animating between two trees is the next piece of Phase 4. The
 * control is here rather than held back so the wire format and the store settle
 * before anything depends on them.
 */
export function TransitionBar() {
  const mode = useStudioStore((state) => state.mode);
  const transition = useStudioStore((state) => state.transition);
  const setTransition = useStudioStore((state) => state.setTransition);
  const goLive = useStudioStore((state) => state.goLive);
  const scenes = useStudioStore((state) => state.scenes);
  const previewSceneId = useStudioStore((state) => state.previewSceneId);
  const activeSceneId = useStudioStore((state) => state.activeSceneId);

  if (mode === 'quick') {
    return null;
  }

  const staged = scenes.find((scene) => scene.id === previewSceneId);
  // Re-taking the scene already on air is allowed and useful: it is how an edit
  // made since the last transition is published. The button says the same word
  // either way, so the line underneath carries the difference.
  const retake = previewSceneId !== null && previewSceneId === activeSceneId;

  return (
    <div className="take">
      <label className="take__field">
        <span className="take__label">Transition</span>
        <select
          className="select select--small"
          value={transition.type}
          onChange={(event) =>
            setTransition({ ...transition, type: event.target.value as TransitionType })
          }
        >
          {TRANSITION_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </label>

      <label className="take__field">
        <span className="take__label">Duration</span>
        <input
          className="input input--small take__ms"
          type="number"
          min={0}
          step={100}
          disabled={transition.type === 'cut'}
          value={transition.duration}
          onChange={(event) =>
            setTransition({ ...transition, duration: Math.max(0, Number(event.target.value) || 0) })
          }
        />
      </label>

      <button type="button" className="take__btn" onClick={goLive} disabled={!previewSceneId}>
        TAKE
      </button>

      <span className="take__hint">
        {!staged
          ? 'Nothing staged'
          : retake
            ? 'Re-publishes what is already on air'
            : `${staged.name} ↓ on air`}
      </span>
    </div>
  );
}
